import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { HiOutlineChevronDown } from "react-icons/hi2";
import { appConfig } from "../../app-config";
import { storeGenericActions } from "../../store/slices/generic";
import { FormSelect, Panel } from "../../ui";
import { cookie } from "../../util";
import { releasesData } from "./releases-data";
import "./release-notes.scss";

const VISIBLE_COUNT = 10;

export const ReleaseNotes = (): JSX.Element => {
  const dispatch = useDispatch();

  useEffect(() => {
    cookie.set("newReleaseChecked", appConfig.version);
    dispatch(storeGenericActions.update({ checkedRelease: appConfig.version }));
  }, [dispatch]);

  // Year filter
  const years = useMemo(() => {
    return Array.from(new Set(releasesData.map((r) => r.year)));
  }, []);

  const latestYear = years[0] || `${new Date().getFullYear()}`;
  const [selectedYear, setSelectedYear] = useState(latestYear);

  const filteredReleases = useMemo(
    () => releasesData.filter((r) => r.year === selectedYear),
    [selectedYear]
  );

  // Expand/collapse
  const firstVersion = filteredReleases[0]?.version || null;
  const [expandedVersion, setExpandedVersion] = useState<string | null>(firstVersion);
  const skipAnimationRef = useRef(false);

  const handleYearChange = (year: string) => {
    skipAnimationRef.current = true;
    const first = releasesData.find((r) => r.year === year);
    setExpandedVersion(first?.version || null);
    setSelectedYear(year);
  };

  useEffect(() => {
    if (skipAnimationRef.current) {
      requestAnimationFrame(() => {
        skipAnimationRef.current = false;
      });
    }
  });

  const toggleExpand = (version: string) => {
    skipAnimationRef.current = false;
    setExpandedVersion((prev) => (prev === version ? null : version));
  };

  // Show more
  const [showAll, setShowAll] = useState(false);
  const visibleReleases = showAll
    ? filteredReleases
    : filteredReleases.slice(0, VISIBLE_COUNT);
  const hasMore = filteredReleases.length > VISIBLE_COUNT;

  const titleRowNode = (
    <div className="release-notes__title-actions">
      <div className="release-notes__tabs">
        {years.map((year) => (
          <button
            key={year}
            className={`release-notes__tab${selectedYear === year ? " release-notes__tab--active" : ""}`}
            onClick={() => handleYearChange(year)}
          >
            {year}
          </button>
        ))}
      </div>
      <div className="release-notes__year-select">
        <FormSelect
          options={years.map((y) => ({ value: y, label: y }))}
          value={selectedYear}
          onChange={(val) => handleYearChange(val as string)}
        />
      </div>
    </div>
  );

  return (
    <Panel title="Release Notes" className="release-notes" nodeInTitleRow={titleRowNode}>
      {/* Release list */}
      <div className="release-notes__list">
        {visibleReleases.map((release) => {
          const isExpanded = expandedVersion === release.version;
          const isCurrent = release.version === appConfig.version;

          return (
            <div
              key={release.version}
              className={`release-notes__item${isExpanded ? " release-notes__item--expanded" : ""}`}
            >
              <div
                className="release-notes__item-header"
                onClick={() => toggleExpand(release.version)}
              >
                <span
                  className={`release-notes__version${isCurrent ? " release-notes__version--current" : ""}`}
                >
                  {release.version}
                </span>
                <span className="release-notes__date">
                  {release.published_at}
                </span>
                {(release.featuresNumber ?? 0) > 0 && (
                  <span className="release-notes__badge release-notes__badge--features">
                    {release.featuresNumber} feature
                    {release.featuresNumber! > 1 ? "s" : ""}
                  </span>
                )}
                {(release.fixesNumber ?? 0) > 0 && (
                  <span className="release-notes__badge release-notes__badge--fixes">
                    {release.fixesNumber} fix
                    {release.fixesNumber! > 1 ? "es" : ""}
                  </span>
                )}
                <HiOutlineChevronDown
                  size={16}
                  className={`release-notes__chevron${isExpanded ? " release-notes__chevron--open" : ""}`}
                />
              </div>

              <div
                className={`release-notes__item-body${isExpanded ? " release-notes__item-body--open" : ""}${skipAnimationRef.current ? " release-notes__item-body--no-animate" : ""}`}
              >
                <div className="release-notes__item-body-inner">
                  <div className="release-notes__item-content">
                    {release.intro && (
                      <p className="release-notes__intro">{release.intro}</p>
                    )}
                    {release.points?.length > 0 && (
                      <ul className="release-notes__points">
                        {release.points.map((point, index) => (
                          <li key={index}>
                            {point.title && <strong>{point.title}</strong>}{" "}
                            {point.description}
                            {point.images?.map((src, i) => (
                              <img
                                key={i}
                                className="release-notes__point-image"
                                src={src}
                                alt={point.title || ""}
                              />
                            ))}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && (
        <div className="release-notes__show-more">
          <button onClick={() => setShowAll(!showAll)}>
            {showAll
              ? "Show less"
              : `Show all ${filteredReleases.length} releases`}
          </button>
        </div>
      )}

    </Panel>
  );
};
